import React, { useState } from 'react';
import T from 'prop-types';
import { values, compact, isEmpty } from 'lodash-es';

import helpers from '../../../../utils/helpers';
import { authOptions } from '../api';
import UserEmailForm from './UserEmailForm';
import UserPasswordForm from './UserPasswordForm';
import UserSocials from './UserSocials';
import Auth0Form from './Auth0Form';

const auth0FormId = 'auth0Form';
const googleFormId = 'google-oauth2';

const initialEmail = (email) =>
  email || new URLSearchParams(window.location.search).get('email') || '';

const errorFromResponse = (err) => {
  const errors = err.response && err.response.data && err.response.data.errors;
  const emailError = errors && errors.email;
  const errorMessage = errors && errors.message;

  if (emailError) return { email: `Email ${emailError[0]}` };
  if (errorMessage) return { email: errorMessage };

  return { email: 'API error' };
};

const submitAuth0Form = (connection) => {
  if (connection) {
    document.forms[auth0FormId].action += `?connection=${connection}`;
  }
  document.forms[auth0FormId].submit();
};

const submitGoogleForm = () => {
  document.forms[googleFormId].submit();
};

const UserAuthFormStep = ({
  links,
  processing,
  setProcessing,
  onUserFormSubmit,
  responseError,
  onForgotClick,
  onConfirmationRequred,
  onPressMagicLink,
  onPressExpired,
  email,
}) => {
  const [error, setError] = useState(responseError || {});
  const [password, setPassword] = useState('');
  const [emailWithDomain, setEmailWithDomain] = useState(() => initialEmail(email));
  const [showEmail, setShowEmail] = useState(true);

  const isFormValid =
    compact(values(error)).length === 0 && emailWithDomain.length > 0 && password.length > 0;

  const setters = {
    setError,
    setEmailWithDomain,
  };

  const handleUserChange = (v, e) => {
    helpers.updateInputValue(e, setters);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setProcessing(true);

    authOptions(emailWithDomain)
      .then(({ data: { method, connection, require_confirmation, press_access_ended_on } }) => {
        setError({});

        if (method === 'auth0') {
          submitAuth0Form(connection);
        } else if (method === 'google_oauth2') {
          submitGoogleForm();
        } else if (method === 'press_magic_link') {
          return onPressMagicLink(emailWithDomain, true);
        } else if (method === 'press_expired') {
          onPressExpired(emailWithDomain, press_access_ended_on);
        } else if (require_confirmation) {
          onConfirmationRequred(emailWithDomain);
        } else {
          setShowEmail(false);
        }

        return null;
      })
      .catch((err) => {
        setError(errorFromResponse(err));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleSubmit = (e) => {
    if (!isFormValid) return;
    setProcessing(true);
    e.preventDefault();

    authOptions(emailWithDomain)
      .then(({ data: { method, connection, require_confirmation } }) => {
        setError({});

        if (method === 'auth0') {
          submitAuth0Form(connection);
        } else if (require_confirmation) {
          onConfirmationRequred(emailWithDomain);
        } else {
          const userData = {
            email: emailWithDomain,
            password,
          };
          onUserFormSubmit(userData);
        }
      })
      .catch((err) => {
        setError(errorFromResponse(err));
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleForgotEmailClick = () => {
    const isEmailValid = isEmpty(error.emailWithDomain);
    onForgotClick(isEmailValid ? emailWithDomain : '');
  };

  return (
    <div className="user-auth-step">
      <h3 className="user-auth-step__title">Sign in to Apptopia</h3>
      <UserEmailForm
        emailWithDomain={emailWithDomain}
        error={error}
        processing={processing}
        handleUserChange={handleUserChange}
        handleEmailSubmit={handleEmailSubmit}
        showEmail={!showEmail}
      />
      <UserPasswordForm
        showEmail={showEmail}
        password={password}
        isFormValid={isFormValid}
        error={responseError}
        processing={processing}
        handleUserChange={handleUserChange}
        handlePasswordChange={setPassword}
        handleSubmit={handleSubmit}
      />

      <UserSocials links={links} showEmail={showEmail} onForgotClick={handleForgotEmailClick} />
      <Auth0Form formId={auth0FormId} links={links} />
    </div>
  );
};

UserAuthFormStep.propTypes = {
  links: T.object.isRequired,
  processing: T.bool.isRequired,
  setProcessing: T.func.isRequired,
  onUserFormSubmit: T.func,
  onForgotClick: T.func,
  onConfirmationRequred: T.func,
  onPressMagicLink: T.func,
  onPressExpired: T.func,
  responseError: T.object,
  email: T.string,
};

export default UserAuthFormStep;
